import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, SecurityContext, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import * as moment from 'moment';
import { Table } from 'primeng/table';
import { ObjectUtils } from 'primeng/utils';
import { AuthService } from 'src/app/auth/auth.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { SearchResultType, VirtualGroupRelation } from 'src/app/shared/app.enums';
import { environment } from 'src/environments/environment';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'ness-crime-gun-shootings',
  templateUrl: './crime-gun-shootings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ObjectUtils]
})
export class CrimeGunShootingsComponent implements OnInit {
  @ViewChild('dt') dt: Table;
  @Input() isPrintView: boolean;
  @Output() viewEventDetails: EventEmitter<string> = new EventEmitter();
  @Output() viewLocationDetails: EventEmitter<{ eventId: string, addressId: string, orgId: string }> = new EventEmitter();
  @Output() viewPersonDetails: EventEmitter<{ eventId: string, personId: string, orgId: string }> = new EventEmitter();
  @Output() viewLEPartnerDetails: EventEmitter<{ eventId: string, lePartnerId: string, orgId: string, isEditable: boolean }> = new EventEmitter();
  @Output() showDataSlideout: EventEmitter<{ data: ServerModels.CrimeGunShootingIncident, type: SearchResultType, dataId: string }> = new EventEmitter();

  cols: any[] = [
    { field: 'EventID', header: 'RMS Data', sortable: false },
    { field: 'NIBINLECaseNumber', header: 'LE Case #', sortable: true },
    { field: 'AgencyName', header: 'LE Agency', sortable: true },
    { field: 'ExhibitCategory', header: 'Exhibit Category', sortable: true },
    { field: 'EventType', header: 'Crime Type', sortable: true },
    { field: 'OccurrenceDate', header: 'Event Date/Time', sortable: true },
    { field: 'RMSEventData.LocationAddress', header: 'Location', sortable: false },
    { field: 'RMSEventData.EventPeople', header: 'Involved People', sortable: false },
    { field: 'RMSEventData.EventLEPartners', header: 'LE Partner', sortable: true },
    { field: 'RelatedCrimeGuns', header: 'Linked Crime Gun(s)', sortable: true },
    { field: 'LabCaseNumber', header: 'NIBIN Case #/Exhibit #', sortable: true}
  ];
  expandCase: string;
  expandedRows: any = {};
  momentObj = moment;
  rows: number = AppConstants.defaultTableRows;
  toggleIconClass: string = 'fas fa-minus-square';
  securityContext = SecurityContext;
  showSidebar: boolean = false;
  analystNotes: { LECaseNumber: string, Notes: string } = {
    LECaseNumber: null,
    Notes: null
  };
  searchResultType = SearchResultType;
  hasUnknownAgency: boolean = false;

  private _disableExpandAllRowsBtn: boolean = true;
  get disableExpandAllRowsBtn(): boolean {
    return this._disableExpandAllRowsBtn;
  }

  set disableExpandAllRowsBtn(b: boolean) {
    this._disableExpandAllRowsBtn = b;
  }

  private _loading: boolean = true;
  get loading(): boolean {
    return this._loading;
  }
  set loading(isLoading: boolean) {
    this._loading = isLoading;
  }

  private _crimeGunShootingIncidents: ServerModels.CrimeGunShootingIncident[] = [];
  @Input() set crimeGunShootingIncidents(shootings: ServerModels.CrimeGunShootingIncident[]) {
    const hasEventData = shootings?.filter(x => !!x.RMSEventData?.EventID)?.length > 0;
    if (!hasEventData) {
      this.allExpanded = false;
    }
    this.disableExpandAllRowsBtn = !hasEventData;
    if (!!shootings) {
      this.hasUnknownAgency = shootings?.some(x => !x.AgencyID);
      this._crimeGunShootingIncidents = [...shootings];
      this.displayPopupMessage(shootings);
    }
  }

  get crimeGunShootingIncidents(): ServerModels.CrimeGunShootingIncident[] {
    return this._crimeGunShootingIncidents;
  }

  private _toggleLessPeople: boolean = true;
  get toggleLessPeople(): boolean {
    return this._toggleLessPeople;
  }

  set toggleLessPeople(b: boolean) {
    this._toggleLessPeople = b;
  }

  private _allExpanded: boolean = false;
  get allExpanded(): boolean {
    return this._allExpanded;
  }
  set allExpanded(b: boolean) {
    this._allExpanded = b;
  }

  constructor(public domSanitizer: DomSanitizer,
    private authService: AuthService,
    public hostElement: ElementRef,
    public messageService: MessageService) { }

  ngOnInit(): void {
    if (this.isPrintView) {
      this.crimeGunShootingIncidents.forEach(function (event) {
        this.expandedRows[event.RMSEventData.EventID] = true;
      }, this);
    } else {
      this.expandedRows = {};
    }  
  }

  public checkExpanded(evt: { originalEvent: Event, data: ServerModels.CrimeGunShootingIncident }): void {
    delete this.expandedRows.null;
    this.allExpanded = Object.keys(this.expandedRows).length > 0 && Object.keys(this.expandedRows).length == this.crimeGunShootingIncidents.filter(x => x.RMSEventData)?.length;
  }

  private _getDataKeyValue(rowData: any) {
    if (!this.dt.dataKey) {
      throw new Error('dataKey must be defined to use row expansion');
    }
    return String(ObjectUtils.resolveFieldData(rowData, this.dt.dataKey));
  }

  public toggleAllRows(): void {
    if (this.dt.rowExpandMode === 'single') {
      throw new Error('Cannot toggle all rows when rowExpandMode is set to single');
    }
    this.allExpanded = !this.allExpanded;
    if (this.allExpanded) {
      const value = this.dt.filteredValue || this.dt.value;
      value.forEach(rowData => {
        let dataKeyValue = this._getDataKeyValue(rowData);
        this.dt.expandedRowKeys[dataKeyValue] = true;
      });
    } else {
      this.dt.expandedRowKeys = {};
    }
  }

  public showAnalystNotes(caseNumber: string, notes: string): void {
    this.analystNotes.LECaseNumber = caseNumber;
    this.analystNotes.Notes = notes;
    this.showSidebar = true;
  }

  public clearSidebar(): void {
    this.analystNotes = {
      LECaseNumber: null,
      Notes: null
    };
  }

  public isRelatedAgency(shooting: ServerModels.CrimeGunShootingIncident): boolean {
    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFHQ'])) {
      return true;
    } else if (this.authService.hasUserType(['ATFFieldDivision'])) {
      return this.authService.hasOrgCode(shooting.FieldDivisionID);
    } else {
      return this.authService.canView(shooting.VirtualGroupRelation);
    }
  }

  public isOutsideAgency(agencyId: string, fieldDivisionId: string): boolean {
    if (!agencyId) {
      return true;
    } else if (this.authService.hasRole(['NESS Administrator'])) {
      return false;
    } else {
      return (this.authService.hasUserType(['StateLocalLE']) ? !this.authService.hasOrgCode(agencyId) :
        this.authService.hasUserType(['ATFFieldDivision']) && !this.authService.hasOrgCode(fieldDivisionId));
    }
  }

  public canAddRmsData(shooting: ServerModels.CrimeGunShootingIncident): boolean {
    if (this.authService.hasRole(['NESS Administrator']))
      return !!shooting.AgencyID;
    else if (this.authService.hasRole(['Viewer']))
      return false;
    else if (!this.isOutsideAgency(shooting.AgencyID, shooting.FieldDivisionID) &&
      (!this.authService.hasUserType(['StateLocalLE'])
        || this.authService.canEdit(shooting.VirtualGroupRelation)))
      return true;
    else
      return false;
  }

  public enableAnalystNotes(shooting: ServerModels.CrimeGunShootingIncident): boolean {
    return this.enableNibinSlideout(shooting);
  }

  public enableNibinSlideout(shooting: ServerModels.CrimeGunShootingIncident): boolean {
    const hasUserType: boolean = this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ']);
    if (!hasUserType) {
      return this.authService.canView(shooting.VirtualGroupRelation);
    }
    return hasUserType;
  }

  public onViewLocationDetails(idx: number, eventData: ServerModels.GunDetailEventData): void {
    const rmsData: { eventId: string, addressId: string, orgId: string } = {
      eventId: eventData.EventID,
      addressId: eventData.EventAddresses[idx].AddressID,
      orgId: this.authService.userType == 'ATFHQ' ? null : this.authService.userType == 'ATFFieldDivision' ? eventData.CGICCode : eventData.AgencyID
    };

    this.viewLocationDetails.emit(rmsData);
  }

  public onViewPersonDetails(idx: number, eventData: ServerModels.GunDetailEventData): void {
    const rmsData: { eventId: string, personId: string, orgId: string } = {
      eventId: eventData.EventID,
      personId: eventData.EventPeople[idx].PersonID,
      orgId: this.authService.userType == 'ATFHQ' ? null : this.authService.userType == 'ATFFieldDivision' ? eventData.CGICCode : eventData.AgencyID
    };

    this.viewPersonDetails.emit(rmsData);
  }

  public onViewLEPartnerDetails(idx: number, shooting: ServerModels.CrimeGunShootingIncident): void {
    const eventData = shooting.RMSEventData;
    const rmsData: { eventId: string, lePartnerId: string, orgId: string, isEditable: boolean } = {
      eventId: eventData.EventID,
      lePartnerId: eventData.EventLEPartners[idx].LEPartnerID,
      orgId: this.authService.userType == 'ATFHQ' ? null : this.authService.userType == 'ATFFieldDivision' ? eventData.CGICCode : eventData.AgencyID,
      isEditable: !this.authService.hasUserType(['StateLocalLE']) || this.authService.canEdit(shooting.VirtualGroupRelation)
    };

    this.viewLEPartnerDetails.emit(rmsData);
  }

  public openRelatedCrimeGun(evt: Event, id: string): void {
    evt.preventDefault();
    const token = sessionStorage.getItem(environment.authTokenStorageKey);
    let url = (<HTMLLinkElement>evt.target).baseURI + '/crimegun/' + id;
    let windowObjRef = window.open(url, '_blank');
    windowObjRef.sessionStorage.setItem(environment.authTokenStorageKey, token);
  }

  public isUniqueFirearm(data: ServerModels.CrimeGunShootingIncident): boolean {
    return data.ExhibitCategory === 'Test Fire' && !data.CrimeGunFirearmIsUnique;
  }

  public displayPopupMessage(shootings: ServerModels.CrimeGunShootingIncident[]) {
    if (shootings.some(x => x.CrimeGunFirearmIsUnique == false)) {
      this.messageService.clear();
      this.messageService.add({ severity: 'warn', summary: ' Multiple Firearms Identified', detail: 'There are multiple firearms linked to this CGID in NIBIN.  To review the firearm information for each Test Fire, click on the NIBIN Case # link to see the NIBIN Case Data slide-out.  Since this data comes from NIBIN, someone from your agency/field division will need to reach out to either the NIBIN Site or NNCTC to review/correct the leads/hits.', sticky: true });
    }
  }

  public toggleClass(state: string): void {
    if (state === 'hide') {
      this.toggleIconClass = 'fas fa-plus-square';
    }
    if (state === 'show') {
      this.toggleIconClass = 'fas fa-minus-square';
    }
  }

}
